import { useMemo, Dispatch, SetStateAction } from 'react'
import { NavigateFunction } from 'react-router-dom'
import {
    FEDEX_CARRIER_TYPE,
    FEDEX_URL,
    SHIPMENTS_TAB,
    UPS_CARRIER_TYPE,
    UPS_URL,
    DELIVERY_STATUSES,
    DHL_EXPRESS_CARRIER_TYPE,
    DHL_EXPRESS_URL,
} from '@Orders/constants'
import { FileNameProcessInterface } from '@Orders/OrderDetailsPage/Status/types'
import { salesOrder_salesOrder_shipments_items as ShipmentItemsType } from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrder'
import { salesOrderLines_salesOrderLines_items_shipmentAndInspection as ShipmentAndInspectionType } from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrderLines'

import { SUMMARY_KEY } from '~/apps/consumer/components/PartViewerModal/logic'
import { TOOLING_PROCESSES_IDS } from '~/lib/partsConstants'
import { OrderedItemsRecordType, SalesOrderLineStateType } from '../types'
import { getTrackingUrl } from '../logic'

export type ToggleModalForPartViewerFuncType = (
    isPartViewerModalVisible: boolean,
    partData?: OrderedItemsRecordType,
    shipment?: ShipmentAndInspectionType | null,
    isDfmReportLinkClicked?: boolean,
) => void

export const renderInspectionStatus = (
    status: string,
    processes: FileNameProcessInterface[],
    renderProductStatus: (productStatus: string) => JSX.Element,
) => {
    const isTooling = processes.some(
        ({ platformProcessId }) =>
            platformProcessId &&
            TOOLING_PROCESSES_IDS?.indexOf(platformProcessId) >= 0,
    )
    const _status = isTooling ? 'na' : status

    return renderProductStatus(_status)
}

export const onRowClick = (
    partData: OrderedItemsRecordType,
    toggleModalForPartViewer: (
        isPartViewerModalVisible: boolean,
        partData?: OrderedItemsRecordType | undefined,
        shipment?: ShipmentAndInspectionType | null | undefined,
    ) => void,
    setActivePage: Dispatch<SetStateAction<string>>,
) => {
    const isTooling = partData?.processes?.some(
        ({ platformProcessId }) =>
            platformProcessId &&
            TOOLING_PROCESSES_IDS?.indexOf(platformProcessId) >= 0,
    )

    if (isTooling) {
        return {}
    }

    return {
        onClick: () => {
            toggleModalForPartViewer(true, partData, partData?.shipments[0])
            setActivePage(SUMMARY_KEY)
        },
    }
}

export const getPartShipmentDetails = (
    partData: SalesOrderLineStateType,
    shipmentDetail: ShipmentItemsType[],
) => {
    const arr: ShipmentItemsType[] = []

    shipmentDetail?.forEach(shipmentDets => {
        let flag = false

        shipmentDets?.shipmentLines?.items?.forEach(item => {
            if (item?.revision?.id === partData?.revision?.id && !flag) {
                flag = true
                arr.push(shipmentDets)
            }
        })
    })
    return arr
}

export const handleTrackShipment = ({
    evt,
    shipmentId,
    toggleModalForPartViewer,
    setSelectedTab,
    setSelectedShipmentId,
    navigate,
}: {
    evt: Event
    shipmentId: string
    toggleModalForPartViewer: ToggleModalForPartViewerFuncType
    setSelectedTab: Dispatch<SetStateAction<string>>
    setSelectedShipmentId: Dispatch<SetStateAction<string>>
    navigate: NavigateFunction
}) => {
    evt.stopPropagation()
    evt.preventDefault()

    toggleModalForPartViewer(false)
    setSelectedTab(SHIPMENTS_TAB)
    setSelectedShipmentId(shipmentId)

    return navigate(`${location.pathname}#${SHIPMENTS_TAB}`)
}

export const useGetShipmentsList = ({
    shipments,
    shipmentsData,
}: {
    shipments: (ShipmentAndInspectionType | null)[]
    shipmentsData?: ShipmentItemsType[]
}) =>
    useMemo(
        () =>
            (shipments || [])
                .filter(shipment => Boolean(shipment))
                .map(shipment => ({
                    ...shipment,
                    trackingUrl:
                        getTrackingUrl({
                            shipmentsData,
                            shipmentId: shipment?.shipmentId || '',
                        }) || '',
                })),
        [shipments, shipmentsData],
    )

export const useGetShipment = ({
    shipments,
}: {
    shipments: OrderedItemsRecordType['shipments']
}) =>
    useMemo(() => {
        const validShipments = shipments.filter(shipmentItem =>
            Boolean(shipmentItem),
        )
        // find the last valid shipment which includes all of inspection photos & docs
        const shipment = validShipments[validShipments.length - 1]

        return shipment
    }, [shipments])

export const getTrackingCodeUrl = (
    carrier: string | null,
    trackingCode: string | null,
) => {
    if (carrier === UPS_CARRIER_TYPE) {
        return `${UPS_URL}${trackingCode}`
    } else if (carrier === FEDEX_CARRIER_TYPE) {
        return `${FEDEX_URL}${trackingCode}`
    } else if (carrier === DHL_EXPRESS_CARRIER_TYPE) {
        return `${DHL_EXPRESS_URL}${trackingCode}`
    }
}

export const getDeliveredDate = (orders: ShipmentItemsType) => {
    let deliveryDate = orders?.trackingData?.estDeliveryDate

    orders.trackingData?.trackingDetails.filter(trackingDets => {
        if (trackingDets.status === DELIVERY_STATUSES.DELIVERED) {
            deliveryDate = trackingDets.datetime
        }
    })
    return deliveryDate
}
